// Client-side mirror of the server's invoice email template default.
// Kept in sync with server/src/lib/billingEmailTemplate.ts — both files
// must be updated together. Lives client-side so the Send-Invoice modal's
// "Reset to built-in template" button can apply it without a round-trip.

export const DEFAULT_INVOICE_EMAIL_BODY = [
  '<table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f5f7fb;padding:24px 0;font-family:Arial,Helvetica,sans-serif;">',
  '<tr><td align="center">',
  '<table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e5e9f2;">',

  '<tr><td style="background:#1a2a4a;padding:22px 28px;color:#ffffff;">',
  '<div style="font-size:18px;font-weight:bold;letter-spacing:0.3px;">{firmName}</div>',
  '<div style="font-size:11px;opacity:0.85;margin-top:3px;text-transform:uppercase;letter-spacing:1.2px;">Tax Invoice</div>',
  '</td></tr>',

  '<tr><td style="padding:28px;color:#2d3748;font-size:14px;line-height:1.65;">',
  '<p style="margin:0 0 12px 0;">Dear <strong>{clientName}</strong>,</p>',
  '<p style="margin:0 0 12px 0;">Thank you for the opportunity to serve you. Please find attached our tax invoice for the professional services rendered.</p>',

  '<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:18px 0;background:#f7f9fc;border:1px solid #e5e9f2;border-radius:6px;">',
  '<tr><td style="padding:12px 16px;border-bottom:1px solid #e5e9f2;color:#6b7280;font-size:11px;text-transform:uppercase;letter-spacing:0.6px;">Invoice Summary</td></tr>',
  '<tr><td style="padding:4px 16px 14px 16px;">',
  '<table cellpadding="0" cellspacing="0" border="0" width="100%" style="font-size:13px;">',
  '<tr><td style="padding:8px 0;color:#6b7280;width:140px;">Invoice Number</td><td style="padding:8px 0;color:#1a2a4a;font-weight:bold;">{invoiceNumber}</td></tr>',
  '<tr><td style="padding:8px 0;color:#6b7280;border-top:1px solid #edf0f5;">Invoice Date</td><td style="padding:8px 0;color:#1a2a4a;border-top:1px solid #edf0f5;">{invoiceDate}</td></tr>',
  '<tr><td style="padding:8px 0;color:#6b7280;border-top:1px solid #edf0f5;">Due Date</td><td style="padding:8px 0;color:#1a2a4a;border-top:1px solid #edf0f5;">{dueDate}</td></tr>',
  '<tr><td style="padding:12px 0 4px 0;color:#6b7280;border-top:1px solid #edf0f5;">Amount Due</td><td style="padding:12px 0 4px 0;color:#1a2a4a;font-weight:bold;font-size:16px;border-top:1px solid #edf0f5;">&#8377;{amount}</td></tr>',
  '</table>',
  '</td></tr>',
  '</table>',

  '<p style="margin:0 0 12px 0;">Kindly arrange payment by the due date. Our bank details and GSTIN are mentioned on the attached invoice.</p>',
  '<p style="margin:0 0 12px 0;">Should you have any questions regarding this invoice, please feel free to reach out to our accounts team — we are always happy to help.</p>',
  '<p style="margin:0 0 12px 0;">We value your continued association with us and look forward to many more years of partnership.</p>',

  '<p style="margin:26px 0 4px 0;">Warm regards,</p>',
  '<p style="margin:0;font-weight:bold;color:#1a2a4a;">{senderName}</p>',
  '<p style="margin:0;color:#6b7280;font-size:12px;">{firmName}</p>',
  '</td></tr>',

  '<tr><td style="background:#f7f9fc;padding:14px 28px;border-top:1px solid #e5e9f2;color:#9ca3af;font-size:11px;text-align:center;line-height:1.5;">',
  'This is a system-generated email. The original tax invoice is attached as a PDF.',
  '</td></tr>',

  '</table>',
  '</td></tr>',
  '</table>',
].join('');

// ─── Batch-send body builder ──────────────────────────────────────────────
// Renders a multi-invoice email with the same branded shell, but the
// "Invoice Summary" card is replaced by a table listing every invoice in
// the batch with a Total Due footer row. Used by the "Send Email" bulk
// action on the Invoice list when ≥ 2 invoices are selected.

interface BatchInvoiceRow {
  invoiceNumber: string;
  invoiceDate: string;        // pre-formatted as dd-MMM-yy
  dueDate: string;            // pre-formatted (or '—')
  totalAmount: number;        // already a number, ready for INR display
}

const inr2 = (n: number) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function buildBatchInvoiceEmailBody(opts: {
  clientName: string;
  firmName: string;
  senderName: string;
  invoices: BatchInvoiceRow[];
}): string {
  const { clientName, firmName, senderName, invoices } = opts;
  const total = invoices.reduce((s, i) => s + Number(i.totalAmount || 0), 0);
  const count = invoices.length;
  const countWord = count === 1 ? 'tax invoice' : `${count} tax invoices`;

  const rows = invoices.map((inv, idx) => [
    '<tr>',
      `<td style="padding:8px 10px;border-bottom:1px solid #edf0f5;color:#6b7280;width:32px;text-align:center;">${idx + 1}</td>`,
      `<td style="padding:8px 10px;border-bottom:1px solid #edf0f5;color:#1a2a4a;font-family:'Courier New',monospace;font-weight:bold;">${inv.invoiceNumber}</td>`,
      `<td style="padding:8px 10px;border-bottom:1px solid #edf0f5;color:#374151;white-space:nowrap;">${inv.invoiceDate}</td>`,
      `<td style="padding:8px 10px;border-bottom:1px solid #edf0f5;color:#374151;white-space:nowrap;">${inv.dueDate || '—'}</td>`,
      `<td style="padding:8px 10px;border-bottom:1px solid #edf0f5;color:#1a2a4a;font-weight:bold;text-align:right;white-space:nowrap;">&#8377;${inr2(inv.totalAmount)}</td>`,
    '</tr>',
  ].join('')).join('');

  return [
    '<table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f5f7fb;padding:24px 0;font-family:Arial,Helvetica,sans-serif;">',
    '<tr><td align="center">',
    '<table cellpadding="0" cellspacing="0" border="0" width="640" style="max-width:640px;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e5e9f2;">',

    '<tr><td style="background:#1a2a4a;padding:22px 28px;color:#ffffff;">',
    `<div style="font-size:18px;font-weight:bold;letter-spacing:0.3px;">${firmName}</div>`,
    '<div style="font-size:11px;opacity:0.85;margin-top:3px;text-transform:uppercase;letter-spacing:1.2px;">Tax Invoices</div>',
    '</td></tr>',

    '<tr><td style="padding:28px;color:#2d3748;font-size:14px;line-height:1.65;">',
    `<p style="margin:0 0 12px 0;">Dear <strong>${clientName}</strong>,</p>`,
    `<p style="margin:0 0 12px 0;">Thank you for the opportunity to serve you. Please find attached ${countWord} for the professional services rendered.</p>`,

    // Invoices summary table
    '<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:18px 0;background:#f7f9fc;border:1px solid #e5e9f2;border-radius:6px;">',
    '<tr><td colspan="5" style="padding:12px 16px;border-bottom:1px solid #e5e9f2;color:#6b7280;font-size:11px;text-transform:uppercase;letter-spacing:0.6px;">Invoices in this batch</td></tr>',
    '<tr><td colspan="5" style="padding:4px 16px 0 16px;">',
    '<table cellpadding="0" cellspacing="0" border="0" width="100%" style="font-size:13px;">',
    '<thead><tr>',
      '<th style="padding:8px 10px;text-align:center;color:#6b7280;font-weight:bold;font-size:11px;text-transform:uppercase;border-bottom:1px solid #e5e9f2;width:32px;">#</th>',
      '<th style="padding:8px 10px;text-align:left;color:#6b7280;font-weight:bold;font-size:11px;text-transform:uppercase;border-bottom:1px solid #e5e9f2;">Invoice #</th>',
      '<th style="padding:8px 10px;text-align:left;color:#6b7280;font-weight:bold;font-size:11px;text-transform:uppercase;border-bottom:1px solid #e5e9f2;">Date</th>',
      '<th style="padding:8px 10px;text-align:left;color:#6b7280;font-weight:bold;font-size:11px;text-transform:uppercase;border-bottom:1px solid #e5e9f2;">Due</th>',
      '<th style="padding:8px 10px;text-align:right;color:#6b7280;font-weight:bold;font-size:11px;text-transform:uppercase;border-bottom:1px solid #e5e9f2;">Amount</th>',
    '</tr></thead>',
    '<tbody>', rows, '</tbody>',
    '<tfoot><tr>',
      '<td colspan="4" style="padding:12px 10px;text-align:right;color:#1a2a4a;font-weight:bold;border-top:2px solid #e5e9f2;">Total Due</td>',
      `<td style="padding:12px 10px;text-align:right;color:#1a2a4a;font-weight:bold;font-size:16px;border-top:2px solid #e5e9f2;">&#8377;${inr2(total)}</td>`,
    '</tr></tfoot>',
    '</table>',
    '</td></tr>',
    '<tr><td colspan="5" style="padding:0 16px 14px 16px;"></td></tr>',
    '</table>',

    '<p style="margin:0 0 12px 0;">Kindly arrange payment by the respective due dates. Our bank details and GSTIN are mentioned on each attached invoice.</p>',
    '<p style="margin:0 0 12px 0;">Should you have any questions, please feel free to reach out to our accounts team — we are always happy to help.</p>',
    '<p style="margin:0 0 12px 0;">We value your continued association with us and look forward to many more years of partnership.</p>',

    '<p style="margin:26px 0 4px 0;">Warm regards,</p>',
    `<p style="margin:0;font-weight:bold;color:#1a2a4a;">${senderName}</p>`,
    `<p style="margin:0;color:#6b7280;font-size:12px;">${firmName}</p>`,
    '</td></tr>',

    '<tr><td style="background:#f7f9fc;padding:14px 28px;border-top:1px solid #e5e9f2;color:#9ca3af;font-size:11px;text-align:center;line-height:1.5;">',
    `This is a system-generated email. ${count === 1 ? 'The original tax invoice is attached as a PDF.' : `All ${count} original tax invoices are attached as PDFs.`}`,
    '</td></tr>',

    '</table>',
    '</td></tr>',
    '</table>',
  ].join('');
}
